/*
 * Copyright 2025 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

/**
 * Knative Service 状态条件
 */
export interface KnativeCondition {
  type: string;
  status: 'True' | 'False' | 'Unknown';
  reason?: string;
  message?: string;
  lastTransitionTime?: string;
}

/**
 * Knative Revision 资源
 */
export interface KnativeRevision {
  metadata: {
    name: string;
    namespace: string;
    uid: string;
    creationTimestamp: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
    ownerReferences?: Array<{
      apiVersion: string;
      kind: string;
      name: string;
      uid: string;
    }>;
  };
  spec: {
    containerConcurrency?: number;
    timeoutSeconds?: number;
    containers?: Array<{
      image: string;
      name?: string;
      ports?: Array<{
        containerPort: number;
        protocol?: string;
      }>;
      resources?: {
        limits?: Record<string, string>;
        requests?: Record<string, string>;
      };
      env?: Array<{
        name: string;
        value?: string;
        valueFrom?: object;
      }>;
    }>;
  };
  status?: {
    conditions?: KnativeCondition[];
    observedGeneration?: number;
    actualReplicas?: number;
    desiredReplicas?: number;
  };
}

/**
 * 流量目标配置
 */
export interface TrafficTarget {
  configurationName?: string;
  revisionName?: string;
  latestRevision?: boolean;
  percent?: number;
  tag?: string;
  url?: string;
}

/**
 * Knative Service 资源
 */
export interface KnativeService {
  apiVersion: string;
  kind: string;
  metadata: {
    name: string;
    namespace: string;
    uid: string;
    creationTimestamp: string;
    generation?: number;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
  spec: {
    template?: {
      metadata?: {
        name?: string;
        labels?: Record<string, string>;
        annotations?: Record<string, string>;
      };
      spec?: {
        containerConcurrency?: number;
        timeoutSeconds?: number;
        containers?: Array<{
          image: string;
          name?: string;
          ports?: Array<{
            containerPort: number;
            protocol?: string;
          }>;
          resources?: {
            limits?: Record<string, string>;
            requests?: Record<string, string>;
          };
          env?: Array<{
            name: string;
            value?: string;
            valueFrom?: object;
          }>;
        }>;
      };
    };
    traffic?: TrafficTarget[];
  };
  status?: {
    url?: string;
    address?: {
      url?: string;
    };
    conditions?: KnativeCondition[];
    latestCreatedRevisionName?: string;
    latestReadyRevisionName?: string;
    traffic?: TrafficTarget[];
    observedGeneration?: number;
  };
}

/**
 * Knative Route 资源
 */
export interface KnativeRoute {
  metadata: {
    name: string;
    namespace: string;
    uid: string;
    creationTimestamp: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
  spec: {
    traffic?: TrafficTarget[];
  };
  status?: {
    url?: string;
    address?: {
      url?: string;
    };
    conditions?: KnativeCondition[];
    traffic?: TrafficTarget[];
  };
}

/**
 * Knative Configuration 资源
 */
export interface KnativeConfiguration {
  metadata: {
    name: string;
    namespace: string;
    uid: string;
    creationTimestamp: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
  spec: {
    template?: {
      metadata?: {
        name?: string;
        labels?: Record<string, string>;
        annotations?: Record<string, string>;
      };
      spec?: {
        containerConcurrency?: number;
        timeoutSeconds?: number;
        containers?: Array<{
          image: string;
          name?: string;
        }>;
      };
    };
  };
  status?: {
    conditions?: KnativeCondition[];
    latestCreatedRevisionName?: string;
    latestReadyRevisionName?: string;
    observedGeneration?: number;
  };
}

/**
 * Pod Autoscaler (KPA) 资源
 */
export interface KnativePodAutoscaler {
  metadata: {
    name: string;
    namespace: string;
    uid: string;
    creationTimestamp: string;
    labels?: Record<string, string>;
    annotations?: Record<string, string>;
  };
  spec: {
    containerConcurrency?: number;
    scaleTargetRef?: {
      apiVersion: string;
      kind: string;
      name: string;
    };
  };
  status?: {
    conditions?: KnativeCondition[];
    actualScale?: number;
    desiredScale?: number;
    metricsServiceName?: string;
    serviceName?: string;
  };
}

/**
 * Knative 服务状态摘要
 */
export type KnativeServiceStatus = 
  | 'Ready'
  | 'NotReady'
  | 'Unknown'
  | 'Progressing';

/**
 * 获取 Knative Service 状态
 */
export function getKnativeServiceStatus(service: KnativeService): KnativeServiceStatus {
  const conditions = service.status?.conditions || [];
  
  const readyCondition = conditions.find(c => c.type === 'Ready');
  if (!readyCondition) {
    return 'Unknown';
  }
  
  if (readyCondition.status === 'True') {
    return 'Ready';
  }
  
  if (readyCondition.status === 'False') {
    return 'NotReady';
  }
  
  // Check if progressing
  const configurationsReadyCondition = conditions.find(
    c => c.type === 'ConfigurationsReady'
  );
  if (configurationsReadyCondition?.status === 'Unknown') {
    return 'Progressing';
  }
  
  return 'Unknown';
}

/**
 * Annotation keys for Knative resources
 */
export const KNATIVE_ANNOTATIONS = {
  SERVICE_NAME: 'knative.dev/service',
  SERVING_NAMESPACE: 'knative.serving/namespace',
  MIN_SCALE: 'autoscaling.knative.dev/min-scale',
  MAX_SCALE: 'autoscaling.knative.dev/max-scale',
  TARGET: 'autoscaling.knative.dev/target',
  SCALE_TO_ZERO_POD_RETENTION: 'autoscaling.knative.dev/scale-to-zero-pod-retention-period',
} as const;

/**
 * Backstage annotation key for Knative Service
 */
export const KNATIVE_SERVICE_ANNOTATION = 'knative.dev/service-name';
export const KNATIVE_NAMESPACE_ANNOTATION = 'knative.dev/service-namespace';

